import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex min-w-0 max-w-full items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap font-[family-name:var(--font-heading)] text-xs font-semibold uppercase tracking-[0.03em] leading-none ring-offset-background transition-[background,box-shadow,border-color,transform,color] duration-200 ease-[var(--ease-out)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-45 active:translate-y-px [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "rounded-[var(--radius-sm)] bg-[var(--vs-red)] text-[var(--text-on-red)] border border-[var(--vs-red)] shadow-[var(--glow-red-sm)] hover:bg-[var(--vs-red-hover,var(--vs-red-bright))] hover:shadow-[var(--glow-red-md)]",
        destructive:
          "rounded-[var(--radius-sm)] bg-transparent text-[var(--danger)] border border-[color-mix(in_srgb,var(--danger)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--danger)_14%,transparent)] hover:border-[var(--danger)]",
        outline:
          "rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-transparent text-[var(--text-primary)] hover:border-[var(--vs-red)] hover:bg-[var(--vs-red-wash)]",
        secondary:
          "rounded-[var(--radius-sm)] bg-[var(--surface-2)] text-[var(--text-primary)] border border-[var(--border-default)] hover:border-[var(--border-strong)]",
        ghost:
          "rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-white/6 hover:text-[var(--text-primary)]",
        link: "text-[var(--vs-red-text)] underline-offset-4 hover:text-[var(--vs-red-text-hover)] hover:underline normal-case tracking-normal font-normal",
      },
      size: {
        default: "h-10 px-5",
        sm: "h-8 rounded-[var(--radius-sm)] px-3.5 text-[11px]",
        /** `sm` plus a 40 px phone floor (WCAG 2.5.8); a caller's h-6/h-7 still wins from `sm`. */
        compact: "h-8 max-sm:h-10 rounded-[var(--radius-sm)] px-3.5 text-[11px]",
        lg: "h-12 rounded-[var(--radius-sm)] px-7 text-sm",
        pill: "h-10 px-6 rounded-[var(--radius-pill)]",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
